// Toast: centered glass pill above the tab bar, optional "Undo" (accent text) or an action button.
//   toast('Removed', { undo: () => restoreCard(id) })      from anywhere (store.ts)
//   <ToastHost />                                          rendered once by the app shell
//   <Toast text="4 cards added to Today" action={{ label: 'See today', run }} />   standalone
import { useApp, dismissToast } from '../store';
import { useScreen, TAB_OF } from '../router';
import { createStore, useStore } from '../lib/createStore';

/** While the chat is hidden to see her (F_Chat), no toast covers her: it shows again after, if it is still due. */
const quiet = createStore<{ on: boolean }>({ on: false });
export function setToastQuiet(on: boolean) { if (quiet.get().on !== on) quiet.set({ on }); }

export interface ToastProps {
  text: string;
  undo?: (() => void) | null;
  action?: { label: string; run: () => void } | null;
  /** CSS bottom (default: above the tab bar on tab screens, else above the home indicator). */
  bottom?: string;
}

export function Toast({ text, undo, action, bottom }: ToastProps) {
  return (
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: bottom || 'calc(var(--xm-bottom) + 76px)', display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 40, padding: '0 12px' }}>
      <div role="status" aria-live="polite" style={{
        pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 12, maxWidth: '100%', padding: action ? '10px 10px 10px 16px' : '10px 12px 10px 16px',
        borderRadius: 14, background: 'rgba(36,33,29,0.98)', border: '1px solid rgba(255,255,255,0.16)', boxShadow: '0 10px 24px rgba(0,0,0,0.45)',
        animation: 'xmRise 300ms ease-out both',
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>{text}</span>
        {undo && (
          <button type="button" onClick={undo} style={{ minHeight: 36, minWidth: 44, padding: '0 6px', color: 'var(--acc)', fontSize: 14, fontWeight: 800, flex: 'none' }}>Undo</button>
        )}
        {!undo && action && (
          <button type="button" onClick={() => { dismissToast(); action.run(); }} style={{ minHeight: 36, padding: '0 10px', borderRadius: 10, background: 'var(--acc)', color: 'var(--on)', fontSize: 13, fontWeight: 800, flex: 'none', whiteSpace: 'nowrap' }}>{action.label}</button>
        )}
      </div>
    </div>
  );
}

/** Renders the store's current toast. Above the tab bar on tab screens (prototype: bottom 108 on 844). */
export function ToastHost() {
  const t = useApp((s) => s.toast);
  const screen = useScreen();
  const hush = useStore(quiet, (s) => s.on);
  if (!t || hush) return null;
  // C: just above the next-card strip / chips and the input bar (F_Chat toast at top 588), so it never covers the
  // input. T: low, just above the tab pill, so it stays off the next card's Mark done · Edit · Later row.
  const bottom = screen === 'C' ? 'calc(var(--xm-tabbar-h) + 114px)' : screen === 'T' ? 'calc(var(--xm-tabbar-h) - 4px)' : TAB_OF[screen] ? 'calc(var(--xm-tabbar-h) + 16px)' : 'calc(var(--xm-bottom) + 76px)';
  return <Toast key={t.id} text={t.text} undo={t.undo} action={t.action} bottom={bottom} />;
}
